"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  RadioGroup,
  Radio,
  HStack,
  Tooltip,
  IconButton,
  FormErrorMessage,
  useToast,
  Text,
} from "@chakra-ui/react";
import { VscCallOutgoing } from "react-icons/vsc";
import { Info, Plus, Trash2 } from "lucide-react";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface TransferCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => void;
  initialValues?: any;
}

export const TransferCallModal: React.FC<TransferCallModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("transfer_call");
  const [description, setDescription] = useState(
    "Transfer the call to a human agent"
  );
  const [destinationType, setDestinationType] = useState<
    "predefined" | "inferred"
  >("predefined");
  const [transferNumber, setTransferNumber] = useState("+12134447777");
  const [transferPrompt, setTransferPrompt] = useState(
    "If the user wants to reach support, transfer to +1 (925) 222-2222; if the user wants to reach sales, transfer to +1 (925) 333-3333"
  );
  const [transferType, setTransferType] = useState<"cold" | "warm">("cold");
  const [displayedPhoneNumber, setDisplayedPhoneNumber] = useState<
    "retell" | "transferee"
  >("retell");
  const [warmTransferType, setWarmTransferType] = useState<
    "static_message" | "prompt"
  >("static_message");
  const [warmTransferMessage, setWarmTransferMessage] = useState("");
  const [warmTransferPrompt, setWarmTransferPrompt] = useState("");
  const [nameError, setNameError] = useState("");
  const [customSipHeaders, setCustomSipHeaders] = useState<
    Array<{ key: string; value: string; error?: string }>
  >([]);

  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToast();

  const validateSipHeaderKey = (key: string) => {
    if (!key.startsWith("X-") && !key.startsWith("x-")) {
      return `Custom SIP header keys must start with X- for key: ${key}`;
    }
    return "";
  };

  const handleAddSipHeader = () => {
    setCustomSipHeaders([
      ...customSipHeaders,
      { key: "", value: "", error: "" },
    ]);
  };

  const handleUpdateSipHeader = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    const updatedHeaders = [...customSipHeaders];
    updatedHeaders[index][field] = value;

    if (field === "key") {
      updatedHeaders[index].error = validateSipHeaderKey(value);
    }

    setCustomSipHeaders(updatedHeaders);
  };

  const handleRemoveSipHeader = (index: number) => {
    const updatedHeaders = customSipHeaders.filter((_, i) => i !== index);
    setCustomSipHeaders(updatedHeaders);
  };

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "transfer_call");
      setDescription(
        initialValues.description || "Transfer the call to a human agent"
      );

      if (initialValues.transfer_destination) {
        setDestinationType(
          initialValues.transfer_destination.type || "predefined"
        );
        if (initialValues.transfer_destination.type === "predefined") {
          setTransferNumber(
            initialValues.transfer_destination.number || "+12134447777"
          );
        } else {
          setTransferPrompt(initialValues.transfer_destination.prompt || "");
        }
      }

      // Handle custom SIP headers
      if (initialValues.custom_sip_headers) {
        const headers = Object.entries(initialValues.custom_sip_headers).map(
          ([key, value]) => ({
            key,
            value: value as string,
            error: validateSipHeaderKey(key),
          })
        );
        setCustomSipHeaders(headers);
      }

      // Determine transfer type and options based on transfer_option
      if (initialValues.transfer_option) {
        if (initialValues.transfer_option.type === "cold_transfer") {
          setTransferType("cold");
          setDisplayedPhoneNumber(
            initialValues.transfer_option.show_transferee_as_caller
              ? "transferee"
              : "retell"
          );
          // Reset warm transfer states
          setWarmTransferType("static_message");
          setWarmTransferMessage("");
          setWarmTransferPrompt("");
        } else if (initialValues.transfer_option.type === "warm_transfer") {
          setTransferType("warm");
          if (initialValues.transfer_option.public_handoff_option) {
            setWarmTransferType(
              initialValues.transfer_option.public_handoff_option.type ||
                "static_message"
            );
            if (
              initialValues.transfer_option.public_handoff_option.type ===
              "static_message"
            ) {
              setWarmTransferMessage(
                initialValues.transfer_option.public_handoff_option.message ||
                  ""
              );
              setWarmTransferPrompt(""); // Reset prompt
            } else {
              setWarmTransferPrompt(
                initialValues.transfer_option.public_handoff_option.prompt || ""
              );
              setWarmTransferMessage(""); // Reset message
            }
          } else {
            // If public_handoff_option is null or undefined
            setWarmTransferType("static_message");
            setWarmTransferMessage("");
            setWarmTransferPrompt("");
          }
          // Reset cold transfer states
          setDisplayedPhoneNumber("retell");
        }
      } else {
        // Default if no transfer_option is provided
        setTransferType("cold");
        setDisplayedPhoneNumber("retell");
        setWarmTransferType("static_message");
        setWarmTransferMessage("");
        setWarmTransferPrompt("");
      }
    }
  }, [initialValues]);

  const validateToolName = (name: string) => {
    if (!name.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }

    if (name.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }

    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }

    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate SIP headers
    const hasInvalidSipHeaders = customSipHeaders.some(
      (header) => header.key && validateSipHeaderKey(header.key)
    );

    if (hasInvalidSipHeaders) {
      toast({
        title: "Invalid SIP header keys",
        description: "All SIP header keys must start with X-",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Convert SIP headers array to object
    const sipHeadersObject = customSipHeaders
      .filter((header) => header.key && header.value)
      .reduce((acc, header) => {
        acc[header.key] = header.value;
        return acc;
      }, {} as Record<string, string>);

    // Construct transfer_option based on transferType
    let transferOption: any;
    if (transferType === "cold") {
      transferOption = {
        type: "cold_transfer",
        show_transferee_as_caller: displayedPhoneNumber === "transferee",
      };
    } else {
      // Warm transfer
      let publicHandoffOption: any = null;
      if (warmTransferType === "static_message") {
        publicHandoffOption = {
          type: "static_message",
          message: warmTransferMessage,
        };
      } else if (warmTransferType === "prompt") {
        publicHandoffOption = {
          type: "prompt",
          prompt: warmTransferPrompt,
        };
      }
      transferOption = {
        type: "warm_transfer",
        public_handoff_option: publicHandoffOption,
      };
    }

    const config = {
      type: "transfer_call",
      name,
      description,
      transfer_destination: {
        type: destinationType,
        ...(destinationType === "predefined"
          ? { number: transferNumber }
          : { prompt: transferPrompt }),
      },
      transfer_option: transferOption, // Use the constructed object
      custom_sip_headers: sipHeadersObject,
    };
    onSave(config);
  };

  const inputProps = {
    bg: isDark ? "gray.700" : "white",
    color: isDark ? "white" : "gray.800",
    borderColor: isDark ? "gray.600" : "gray.300",
    _hover: { borderColor: isDark ? "gray.500" : "gray.400" },
  };

  const labelColor = isDark ? "gray.300" : "gray.700";
  const iconColor = isDark ? "gray.300" : "gray.700";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent
        bg={isDark ? "gray.800" : "white"}
        textColor={isDark ? "white" : "gray.800"}
      >
        <ModalHeader style={{ display: "flex", alignItems: "center" }}>
          <VscCallOutgoing size={20} style={{ marginRight: "8px" }} />
          <span>Transfer Call</span>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color={labelColor}>Name</FormLabel>
              <Input
                id="transfer-call-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter name"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                {...inputProps}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl>
              <FormLabel color={labelColor}>Description (Optional)</FormLabel>
              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                id="transfer-call-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                {...inputProps}
                variant="outline"
                rows={3}
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={labelColor}>Transfer to</FormLabel>
              <RadioGroup
                value={destinationType}
                onChange={setDestinationType}
                colorScheme={isDark ? "white" : "black"}
              >
                <HStack spacing={4}>
                  <Radio
                    value="predefined"
                    sx={{
                      borderWidth: "2px",
                      borderColor: isDark ? "gray.500" : "gray.400",
                      borderRadius: "full",
                      width: "15px",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      _checked: {
                        bg: isDark ? "gray.700" : "gray.200",
                        borderColor: isDark ? "white" : "black",
                        position: "relative",
                        _after: {
                          content: '""',
                          position: "absolute",
                          width: "7px",
                          height: "7px",
                          bg: "gray.500",
                          borderRadius: "full",
                        },
                      },
                    }}
                  >
                    Static Number
                  </Radio>
                  <Radio
                    value="inferred"
                    sx={{
                      borderWidth: "2px",
                      borderColor: isDark ? "gray.500" : "gray.400",
                      borderRadius: "full",
                      width: "15px",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      _checked: {
                        bg: isDark ? "gray.700" : "gray.200",
                        borderColor: isDark ? "white" : "black",
                        position: "relative",
                        _after: {
                          content: '""',
                          position: "absolute",
                          width: "7px",
                          height: "7px",
                          bg: "gray.500",
                          borderRadius: "full",
                        },
                      },
                    }}
                  >
                    Dynamic Routing
                  </Radio>
                </HStack>
              </RadioGroup>

              {destinationType === "predefined" ? (
                <Input
                  id="transfer-call-static-number"
                  mt={2}
                  value={transferNumber}
                  onChange={(e) => setTransferNumber(e.target.value)}
                  placeholder="+12134447777"
                  _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                  textColor={isDark ? "white" : "gray.800"}
                  bg={isDark ? "gray.700" : "white"}
                  color={isDark ? "white" : "gray.800"}
                  borderColor={isDark ? "gray.600" : "gray.300"}
                  _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                  {...inputProps}
                  variant="outline"
                  _focus={{
                    borderColor: isDark ? "gray.100" : "gray.800",
                  }}
                />
              ) : (
                <Textarea
                  sx={
                    isDark
                      ? {
                          "&::-webkit-scrollbar": {
                            height: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#2D3748", // Dark track color
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#4A5568", // Dark thumb color
                            borderRadius: "2px",
                          },
                          // For Firefox
                          scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                          _hover: {
                            scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                          },
                        }
                      : {
                          "&::-webkit-scrollbar": {
                            height: "4px",
                          },
                          "&::-webkit-scrollbar-button": {
                            display: "none",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#fff",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#fff",
                            borderRadius: "2px",
                          },
                          scrollbarColor: "rgb(220, 220, 220) white",
                          _hover: {
                            scrollbarColor: "rgb(220, 220, 220) white",
                          },
                        }
                  }
                  id="transfer-call-dynamic-routing"
                  mt={2}
                  value={transferPrompt}
                  onChange={(e) => setTransferPrompt(e.target.value)}
                  placeholder="Enter prompt to infer the destination number. Use {{_}} to add variables."
                  _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                  textColor={isDark ? "white" : "gray.800"}
                  bg={isDark ? "gray.700" : "white"}
                  color={isDark ? "white" : "gray.800"}
                  borderColor={isDark ? "gray.600" : "gray.300"}
                  _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                  rows={4}
                  {...inputProps}
                  variant="outline"
                  _focus={{
                    borderColor: isDark ? "gray.100" : "gray.800",
                  }}
                />
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={labelColor}>Type</FormLabel>
              <RadioGroup
                value={transferType}
                onChange={(value) => {
                  setTransferType(value as "cold" | "warm");
                  if (value === "cold") {
                    // Reset warm transfer states
                    setWarmTransferType("static_message");
                    setWarmTransferMessage("");
                    setWarmTransferPrompt("");
                  } else {
                    // Reset cold transfer states
                    setDisplayedPhoneNumber("retell");
                  }
                }}
                colorScheme={isDark ? "white" : "black"}
              >
                <HStack spacing={4}>
                  <Radio
                    value="cold"
                    sx={{
                      borderWidth: "2px",
                      borderColor: isDark ? "gray.500" : "gray.400",
                      borderRadius: "full",
                      width: "15px",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      _checked: {
                        bg: isDark ? "gray.700" : "gray.200",
                        borderColor: isDark ? "white" : "black",
                        position: "relative",
                        _after: {
                          content: '""',
                          position: "absolute",
                          width: "7px",
                          height: "7px",
                          bg: "gray.500",
                          borderRadius: "full",
                        },
                      },
                    }}
                  >
                    Cold Transfer
                    <Tooltip label="AI transfers the call to the next agent without a debrief.">
                      <IconButton
                        aria-label="Cold transfer info"
                        icon={<Info size={14} />}
                        size="xs"
                        variant="ghost"
                        ml={1}
                        color={iconColor}
                      />
                    </Tooltip>
                  </Radio>
                  <Radio
                    value="warm"
                    sx={{
                      borderWidth: "2px",
                      borderColor: isDark ? "gray.500" : "gray.400",
                      borderRadius: "full",
                      width: "15px",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      _checked: {
                        bg: isDark ? "gray.700" : "gray.200",
                        borderColor: isDark ? "white" : "black",
                        position: "relative",
                        _after: {
                          content: '""',
                          position: "absolute",
                          width: "7px",
                          height: "7px",
                          bg: "gray.500",
                          borderRadius: "full",
                        },
                      },
                    }}
                  >
                    Warm Transfer
                    <Tooltip label="AI provides a debrief to the next agent after transferring the call.">
                      <IconButton
                        aria-label="Warm transfer info"
                        icon={<Info size={14} />}
                        size="xs"
                        variant="ghost"
                        ml={1}
                        color={iconColor}
                      />
                    </Tooltip>
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            {transferType === "cold" && (
              <FormControl isRequired>
                <FormLabel color={labelColor}>Displayed Phone Number</FormLabel>
                <RadioGroup
                  value={displayedPhoneNumber}
                  onChange={setDisplayedPhoneNumber}
                  colorScheme={isDark ? "white" : "black"}
                >
                  <HStack spacing={4}>
                    <Radio
                      value="retell"
                      sx={{
                        borderWidth: "2px",
                        borderColor: isDark ? "gray.500" : "gray.400",
                        borderRadius: "full",
                        width: "15px",
                        height: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        _checked: {
                          bg: isDark ? "gray.700" : "gray.200",
                          borderColor: isDark ? "white" : "black",
                          position: "relative",
                          _after: {
                            content: '""',
                            position: "absolute",
                            width: "7px",
                            height: "7px",
                            bg: "gray.500",
                            borderRadius: "full",
                          },
                        },
                      }}
                    >
                      Agent's Number
                    </Radio>
                    <Radio
                      value="transferee"
                      sx={{
                        borderWidth: "2px",
                        borderColor: isDark ? "gray.500" : "gray.400",
                        borderRadius: "full",
                        width: "15px",
                        height: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        _checked: {
                          bg: isDark ? "gray.700" : "gray.200",
                          borderColor: isDark ? "white" : "black",
                          position: "relative",
                          _after: {
                            content: '""',
                            position: "absolute",
                            width: "7px",
                            height: "7px",
                            bg: "gray.500",
                            borderRadius: "full",
                          },
                        },
                      }}
                    >
                      Transferee's Number
                      <Tooltip label="Original Caller or Callee. If you are using custom telephony, enable SIP REFER and PSTN transfer, and set SIP REFER to show transferee's number.">
                        <IconButton
                          aria-label="Transferee's number info"
                          icon={<Info size={14} />}
                          size="xs"
                          variant="ghost"
                          ml={1}
                          color={iconColor}
                        />
                      </Tooltip>
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}

            {transferType === "warm" && (
              <FormControl isRequired>
                <FormLabel color={labelColor}>Handoff Message</FormLabel>
                <RadioGroup
                  value={warmTransferType}
                  onChange={setWarmTransferType}
                  colorScheme={isDark ? "white" : "black"}
                >
                  <HStack spacing={4}>
                    <Radio
                      value="static_message"
                      sx={{
                        borderWidth: "2px",
                        borderColor: isDark ? "gray.500" : "gray.400",
                        borderRadius: "full",
                        width: "15px",
                        height: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        _checked: {
                          bg: isDark ? "gray.700" : "gray.200",
                          borderColor: isDark ? "white" : "black",
                          position: "relative",
                          _after: {
                            content: '""',
                            position: "absolute",
                            width: "7px",
                            height: "7px",
                            bg: "gray.500",
                            borderRadius: "full",
                          },
                        },
                      }}
                    >
                      Static Sentence
                    </Radio>
                    <Radio
                      value="prompt"
                      sx={{
                        borderWidth: "2px",
                        borderColor: isDark ? "gray.500" : "gray.400",
                        borderRadius: "full",
                        width: "15px",
                        height: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        _checked: {
                          bg: isDark ? "gray.700" : "gray.200",
                          borderColor: isDark ? "white" : "black",
                          position: "relative",
                          _after: {
                            content: '""',
                            position: "absolute",
                            width: "7px",
                            height: "7px",
                            bg: "gray.500",
                            borderRadius: "full",
                          },
                        },
                      }}
                    >
                      Prompt
                    </Radio>
                  </HStack>
                </RadioGroup>

                {warmTransferType === "static_message" ? (
                  <Textarea
                    sx={
                      isDark
                        ? {
                            "&::-webkit-scrollbar": {
                              height: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#2D3748", // Dark track color
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#4A5568", // Dark thumb color
                              borderRadius: "2px",
                            },
                            // For Firefox
                            scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                            _hover: {
                              scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                            },
                          }
                        : {
                            "&::-webkit-scrollbar": {
                              height: "4px",
                            },
                            "&::-webkit-scrollbar-button": {
                              display: "none",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#fff",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#fff",
                              borderRadius: "2px",
                            },
                            scrollbarColor: "rgb(220, 220, 220) white",
                            _hover: {
                              scrollbarColor: "rgb(220, 220, 220) white",
                            },
                          }
                    }
                    mt={2}
                    value={warmTransferMessage}
                    onChange={(e) => setWarmTransferMessage(e.target.value)}
                    placeholder="Enter static message"
                    _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                    textColor={isDark ? "white" : "gray.800"}
                    bg={isDark ? "gray.700" : "white"}
                    color={isDark ? "white" : "gray.800"}
                    borderColor={isDark ? "gray.600" : "gray.300"}
                    _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                    rows={3}
                    {...inputProps}
                    variant="outline"
                    _focus={{
                      borderColor: isDark ? "gray.100" : "gray.800",
                    }}
                  />
                ) : (
                  <Textarea
                    sx={
                      isDark
                        ? {
                            "&::-webkit-scrollbar": {
                              height: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#2D3748", // Dark track color
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#4A5568", // Dark thumb color
                              borderRadius: "2px",
                            },
                            // For Firefox
                            scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                            _hover: {
                              scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                            },
                          }
                        : {
                            "&::-webkit-scrollbar": {
                              height: "4px",
                            },
                            "&::-webkit-scrollbar-button": {
                              display: "none",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#fff",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#fff",
                              borderRadius: "2px",
                            },
                            scrollbarColor: "rgb(220, 220, 220) white",
                            _hover: {
                              scrollbarColor: "rgb(220, 220, 220) white",
                            },
                          }
                    }
                    mt={2}
                    value={warmTransferPrompt}
                    onChange={(e) => setWarmTransferPrompt(e.target.value)}
                    placeholder="Say hello to the agent and summarize the user problem to him"
                    _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                    textColor={isDark ? "white" : "gray.800"}
                    bg={isDark ? "gray.700" : "white"}
                    color={isDark ? "white" : "gray.800"}
                    borderColor={isDark ? "gray.600" : "gray.300"}
                    _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                    rows={3}
                    {...inputProps}
                    variant={"outline"}
                    _focus={{
                      borderColor: isDark ? "gray.100" : "gray.800",
                    }}
                  />
                )}
              </FormControl>
            )}

            <FormControl>
              <FormLabel color={labelColor}>Custom SIP Headers</FormLabel>
              <Text fontSize="sm" color="gray.500" mb={3}>
                Add key/value pairs for call routing, metadata, or carrier
                integration.
              </Text>

              {customSipHeaders.map((header, index) => (
                <VStack key={index} spacing={1} mb={3} align="stretch">
                  <HStack spacing={2}>
                    <FormControl isInvalid={!!header.error} flex={1}>
                      <Input
                        placeholder="Key"
                        value={header.key}
                        onChange={(e) =>
                          handleUpdateSipHeader(index, "key", e.target.value)
                        }
                        _placeholder={{
                          color: isDark ? "gray.400" : "gray.600",
                        }}
                        {...inputProps}
                        variant="outline"
                        _focus={{
                          borderColor: isDark ? "gray.100" : "gray.800",
                        }}
                      />
                    </FormControl>
                    <Input
                      placeholder="Value"
                      value={header.value}
                      onChange={(e) =>
                        handleUpdateSipHeader(index, "value", e.target.value)
                      }
                      _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                      {...inputProps}
                      variant="outline"
                      flex={1}
                      _focus={{
                        borderColor: isDark ? "gray.100" : "gray.800",
                      }}
                    />
                    <IconButton
                      aria-label="Remove header"
                      icon={<Trash2 size={16} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveSipHeader(index)}
                      color={iconColor}
                      flexShrink={0}
                    />
                  </HStack>
                  {header.error && (
                    <Text color="red.500" fontSize="xs" ml={0}>
                      {header.error}
                    </Text>
                  )}
                </VStack>
              ))}

              <Button
                leftIcon={<Plus size={16} />}
                variant="ghost"
                size="sm"
                onClick={handleAddSipHeader}
                color={isDark ? "gray.300" : "gray.600"}
                _hover={{
                  bg: isDark ? "gray.700" : "gray.100",
                }}
              >
                Add
              </Button>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter
          borderTop="1px solid"
          borderColor={isDark ? "gray.700" : "gray.200"}
        >
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "white" : "gray.800"}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSave}
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "white" : "gray.800"}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
